import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import MainLayout from "../../../components/layout/MainLayout";
import { useMasterData } from "../../../hooks/useMasterData";
import { usePermissions } from "../../../contexts/PermissionContext";
import { feesApi } from "../api/feesApi";

type Mode = "STUDENT" | "CLASS";

type DiscountRow = {
  id: string;
  studentId: string;
  sessionId: string;
  type: "FIXED" | "PERCENTAGE";
  value: number;
  amount: number;
  reason: string;
  status: "ACTIVE" | "REVERSED";
  effectiveDate: string;
  createdAt: string;
  student?: {
    firstName?: string;
    lastName?: string;
    scholarNumber?: string;
  } | null;
  class?: { id: string; name: string } | null;
  section?: { id: string; name: string } | null;
  session?: { id: string; name: string } | null;
};

const FeeDiscountManagementPage: React.FC = () => {
  const { masters, fetchAcademicSessions, fetchMasters, fetchSections } = useMasterData();
  const { hasAnyPermission } = usePermissions();

  const [mode, setMode] = useState<Mode>("STUDENT");

  const [sessionId, setSessionId] = useState("");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [sections, setSections] = useState<Array<{ id: string; name: string }>>([]);
  const [statusFilter, setStatusFilter] = useState("");

  const [studentSearch, setStudentSearch] = useState("");
  const [students, setStudents] = useState<any[]>([]);
  const [studentId, setStudentId] = useState("");

  const [discountType, setDiscountType] = useState<"FIXED" | "PERCENTAGE">("FIXED");
  const [discountValue, setDiscountValue] = useState("");
  const [reason, setReason] = useState("");
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [applyTo, setApplyTo] = useState<"ALL" | "UNPAID_ONLY">("ALL");

  const [rows, setRows] = useState<DiscountRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [reverseId, setReverseId] = useState("");
  const [reverseReason, setReverseReason] = useState("");
  const [reverseOpen, setReverseOpen] = useState(false);
  const [reverseLoading, setReverseLoading] = useState(false);

  const canView = useMemo(
    () => hasAnyPermission(["FEES_DISCOUNT_VIEW", "FEES_VIEW", "FEE_VIEW", "FEE_MANAGE"]),
    [hasAnyPermission],
  );
  const canApply = useMemo(
    () => hasAnyPermission(["FEES_DISCOUNT_APPLY", "FEE_MANAGE", "FEES_MANAGE"]),
    [hasAnyPermission],
  );
  const canReverse = useMemo(
    () => hasAnyPermission(["FEES_DISCOUNT_REVERSE", "FEE_MANAGE", "FEES_MANAGE"]),
    [hasAnyPermission],
  );

  useEffect(() => {
    fetchMasters();
    fetchAcademicSessions();
  }, [fetchMasters, fetchAcademicSessions]);

  useEffect(() => {
    const active = masters.academicSessions.find((s) => s.isActive);
    if (!sessionId && active) {
      setSessionId(active.id);
    }
  }, [masters.academicSessions, sessionId]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!classId) {
        setSections([]);
        setSectionId("");
        return;
      }
      const res = await fetchSections(classId);
      if (!cancelled) {
        setSections(Array.isArray(res) ? res : []);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [classId, fetchSections]);

  const selectedStudent = useMemo(
    () => students.find((s) => s.studentId === studentId) || null,
    [students, studentId],
  );

  const loadDiscounts = async () => {
    if (!canView || !sessionId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await feesApi.discounts({
        sessionId,
        classId: classId || undefined,
        sectionId: sectionId || undefined,
        status: statusFilter || undefined,
        search: studentSearch || undefined,
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to load discounts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      loadDiscounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const searchStudents = async () => {
    if (!sessionId) {
      setError("Session is required");
      return;
    }
    setError(null);
    try {
      const data = await feesApi.students({
        sessionId,
        classId: classId || undefined,
        sectionId: sectionId || undefined,
        search: studentSearch || undefined,
      });
      setStudents(Array.isArray(data) ? data : []);
      if (!Array.isArray(data) || data.length === 0) {
        setMessage("No students found for the search/filter");
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to search students");
    }
  };

  const validate = () => {
    if (!sessionId) return "Session is required";
    if (!discountValue || Number(discountValue) <= 0) return "Discount value must be greater than 0";
    if (!reason.trim()) return "Reason is required";

    if (mode === "STUDENT" && !studentId) {
      return "Student is required";
    }
    if (mode === "CLASS" && !classId) {
      return "Class is required for class-wise discount";
    }

    return null;
  };

  const submit = async () => {
    if (!canApply) return;

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError(null);
    setMessage(null);

    try {
      if (mode === "STUDENT") {
        const result = await feesApi.discount({
          studentId,
          sessionId,
          discountType,
          value: Number(discountValue),
          reason: reason.trim(),
          effectiveDate,
          applyToAll: true,
        });
        setMessage(`Student discount applied: ${result?.discount?.amount ?? "Done"}`);
      } else {
        const result = await feesApi.discountClass({
          sessionId,
          classId,
          sectionId: sectionId || undefined,
          discountType,
          value: Number(discountValue),
          applyTo,
          reason: reason.trim(),
          effectiveDate,
        });
        setMessage(
          `Class discount completed. Success: ${result?.success || 0}, Failed: ${result?.failed || 0}, Skipped: ${result?.skipped || 0}`,
        );
      }

      await loadDiscounts();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to apply discount");
    } finally {
      setSubmitting(false);
    }
  };

  const openReverse = (id: string) => {
    setReverseId(id);
    setReverseReason("");
    setReverseOpen(true);
  };

  const reverse = async () => {
    if (!reverseId) return;

    setReverseLoading(true);
    setError(null);

    try {
      await feesApi.reverseDiscount(reverseId, { reason: reverseReason || undefined });
      setReverseOpen(false);
      setMessage("Discount reversed successfully");
      await loadDiscounts();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to reverse discount");
    } finally {
      setReverseLoading(false);
    }
  };

  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
          Fee Discount Management
        </Typography>

        {!canView && (
          <Alert severity="error" sx={{ mb: 2 }}>
            You do not have permission to view fee discount data.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {message && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>
            {message}
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Filter Panel
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Academic Session"
                value={sessionId}
                onChange={(e) => setSessionId(e.target.value)}
              >
                {masters.academicSessions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Class"
                value={classId}
                onChange={(e) => setClassId(e.target.value)}
              >
                <MenuItem value="">All Classes</MenuItem>
                {masters.classes.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Section"
                value={sectionId}
                onChange={(e) => setSectionId(e.target.value)}
                disabled={!classId}
              >
                <MenuItem value="">All Sections</MenuItem>
                {sections.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                <MenuItem value="REVERSED">REVERSED</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={9}>
              <TextField
                fullWidth
                label="Student Search (Name / Scholar No)"
                value={studentSearch}
                onChange={(e) => setStudentSearch(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack direction="row" spacing={1}>
                <Button fullWidth variant="outlined" onClick={searchStudents}>
                  Search Student
                </Button>
                <Button fullWidth variant="contained" onClick={loadDiscounts} disabled={loading}>
                  Refresh
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Discount Form
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={mode}
              onChange={(_, value) => value && setMode(value)}
              size="small"
            >
              <ToggleButton value="STUDENT">Student-wise</ToggleButton>
              <ToggleButton value="CLASS">Class-wise</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <Grid container spacing={2}>
            {mode === "STUDENT" && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Student"
                    value={studentId}
                    onChange={(e) => setStudentId(e.target.value)}
                  >
                    {students.map((s) => (
                      <MenuItem key={s.studentId} value={s.studentId}>
                        {`${s.firstName || ""} ${s.lastName || ""}`.trim()} ({s.scholarNumber || "-"})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Class - Section"
                    value={selectedStudent ? `${selectedStudent?.class?.name || "-"} - ${selectedStudent?.section?.name || "-"}` : ""}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </>
            )}

            {mode === "CLASS" && (
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Apply To"
                  value={applyTo}
                  onChange={(e) => setApplyTo(e.target.value as "ALL" | "UNPAID_ONLY")}
                >
                  <MenuItem value="ALL">All Students</MenuItem>
                  <MenuItem value="UNPAID_ONLY">Only Unpaid Students</MenuItem>
                </TextField>
              </Grid>
            )}

            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Discount Type"
                value={discountType}
                onChange={(e) => setDiscountType(e.target.value as "FIXED" | "PERCENTAGE")}
              >
                <MenuItem value="FIXED">Fixed Amount</MenuItem>
                <MenuItem value="PERCENTAGE">Percentage</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Discount Value"
                type="number"
                value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Effective Date"
                type="date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={9}>
              <TextField
                fullWidth
                label="Reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason is mandatory"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button variant="contained" fullWidth disabled={submitting || !canApply} onClick={submit}>
                {submitting ? "Processing..." : mode === "STUDENT" ? "Apply Student Discount" : "Apply Class Discount"}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Discount Table
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Student</TableCell>
                <TableCell>Scholar No</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{`${row.student?.firstName || ""} ${row.student?.lastName || ""}`.trim() || "-"}</TableCell>
                  <TableCell>{row.student?.scholarNumber || "-"}</TableCell>
                  <TableCell>{`${row.class?.name || "-"} - ${row.section?.name || "-"}`}</TableCell>
                  <TableCell>{Number(row.amount || 0).toFixed(2)}</TableCell>
                  <TableCell>{row.type}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.status}
                      color={row.status === "ACTIVE" ? "success" : "default"}
                    />
                  </TableCell>
                  <TableCell>{String(row.effectiveDate || row.createdAt || "").slice(0, 10)}</TableCell>
                  <TableCell>{row.reason || "-"}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      disabled={!canReverse || row.status !== "ACTIVE"}
                      onClick={() => openReverse(row.id)}
                    >
                      Reverse
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!rows.length && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography variant="body2" color="text.secondary">
                      No discounts found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>

        <Dialog open={reverseOpen} onClose={() => setReverseOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Reverse Discount</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Reversal Reason"
              value={reverseReason}
              onChange={(e) => setReverseReason(e.target.value)}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReverseOpen(false)}>Close</Button>
            <Button color="error" variant="contained" onClick={reverse} disabled={reverseLoading || !canReverse}>
              {reverseLoading ? "Reversing..." : "Reverse"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </MainLayout>
  );
};

export default FeeDiscountManagementPage;
